import { RemoteParticipant, Room, Track } from 'livekit-client'
import { env } from '@/services/EnvironmentVariables'
import * as Constants from '@/services/Constants'
import { MusePeer } from '@/services/RoomContextMusePeer'
import { logger } from '@/services/Logger'

const log = logger.createChildLogger({ name: 'VideoSubscriptionManager' })

class VideoSubscriptionManager {
    private sfuRoom: Room | null = null

    // Items that have not changed gets pushed down the list.
    private list: { sessionId: string; isVisible: boolean | null }[] = []
    private peerInfoMap: Record<string, { username: string }> = {}

    constructor() {}

    setSfuRoom(sfuRoom: Room) {
        this.sfuRoom = sfuRoom
    }

    syncPeersList(peers: Record<string, MusePeer>) {
        Object.keys(peers).forEach((sessionId) => {
            this.peerInfoMap[sessionId] = { username: peers[sessionId]?.username }
        })
    }

    setVisible(sessionId: string) {
        this.setValueAndUpdateRank(sessionId, true)
    }

    setInvisible(sessionId: string) {
        this.setValueAndUpdateRank(sessionId, false)
    }

    setNoVideo(sessionId: string) {
        this.setValueAndUpdateRank(sessionId, null)
    }

    getRemovalCandidates() {
        const topK = Constants.data.max_video_subscription_users
        if (this.list.length < topK) {
            return []
        }

        if (log.isVerbose()) {
            const listForLog = this.toJSON().map((a) => JSON.stringify(a))
            log.verbose('getRemovalCandidates', `starting list=` + listForLog.join(',\n'))
        }

        const trues = this.list.filter((item) => item.isVisible === true)
        const falses = this.list.filter((item) => item.isVisible !== true)
        // get the elements that are NOT `topK` elements from the list.
        const bottomN = [...trues, ...falses].slice(topK)
        return bottomN.map((item) => item.sessionId)
    }

    getSubscribedVideoCount() {
        if (!this.sfuRoom) {
            return 0
        }

        let ct = 0
        this.sfuRoom.remoteParticipants.forEach((p) => {
            p.videoTrackPublications.forEach((pub) => {
                if (pub.source === Track.Source.ScreenShare) {
                    return
                }

                if (pub.isSubscribed) {
                    ct++
                }
            })
        })

        log.verbose('getSubscribedVideoCount', `subscribed video count: ${ct}`)

        return ct
    }

    isVideoVisible(sessionId: string): boolean | null {
        return this.list.find((a) => a.sessionId === sessionId)?.isVisible ?? null
    }

    applyVideoSubscriptionLimit() {
        if (!this.sfuRoom) {
            return
        }

        // get current number of video subscriptions
        const candidates = videoSubscriptionManager.getRemovalCandidates()
        if (candidates.length === 0) {
            return
        }

        log.verbose('applyVideoSubscriptionLimit', `got ${candidates.length} candidates to remove.`)

        let logstr = ''

        let unsubscribedCount = 0
        candidates.forEach((sessionId) => {
            if (!this.sfuRoom) return

            const peer = this.sfuRoom.getParticipantByIdentity(sessionId)
            if (peer && peer instanceof RemoteParticipant) {
                peer.videoTrackPublications.forEach((pub) => {
                    if (pub.source === Track.Source.ScreenShare) {
                        return
                    }

                    const username = this.peerInfoMap[sessionId]?.username || '?'
                    if (pub.isSubscribed) {
                        logstr += `\n    - unsubscribing to video track for sessionId=${sessionId} (username=${username}).`
                        pub.setSubscribed(false)
                    } else {
                        logstr += `\n    - unsubscribing to video track for sessionId=${sessionId} (username=${username}) -- already unsubscribed.`
                    }
                })
                unsubscribedCount++
            }
        })

        this.sfuRoom.remoteParticipants.forEach((p) => {
            p.videoTrackPublications.forEach((pub) => {
                if (pub.source === Track.Source.ScreenShare) {
                    return
                }

                if (pub.isSubscribed) {
                    // does this user exist in peers[]?
                    if (!this.peerInfoMap[p.identity]) {
                        logstr += `\n    - '${p.identity}' does not exist in the peer list; unsubscribing.`
                        pub.setSubscribed(false)
                    }
                }
            })
        })

        if (logstr) {
            log.verbose('applyVideoSubscriptionLimit', `${logstr}`)
        }

        // setTimeout(() => {
        //     const currVideoSubsCt = videoSubscriptionManager.getSubscribedVideoCount()
        //     console.log(`VideoSubscriptionManager: currVideoSubsCt=${currVideoSubsCt}.`)
        // }, 1)
    }

    clear() {
        this.list = []
        this.peerInfoMap = {}
    }

    private setValueAndUpdateRank(sessionId: string, isVisible: boolean | null) {
        const i = this.list.findIndex((item) => item.sessionId === sessionId)
        if (i >= 0) {
            const item = this.list[i]
            if (item.isVisible === isVisible) {
                // no-op
            } else {
                item.isVisible = isVisible
                this.list.splice(i, 1)
                this.list.unshift(item) // move to the front

                log.verbose(
                    'setValueAndUpdateRank',
                    `(sessionId: ${sessionId}, ` +
                        `username: ${this.peerInfoMap[sessionId]?.username}) ` +
                        `video visibility state changed to ${isVisible}.`
                )

                if (isVisible === true && !env.TEST_NO_SUBSCRIBE_MEDIA) {
                    this.sfuResubscribeRemotePeerVideo(sessionId)
                }
            }
        } else {
            // peer not found; add it to the list.
            this.list.unshift({ sessionId, isVisible })
        }
    }

    private sfuResubscribeRemotePeerVideo = (sessionId: string) => {
        if (!this.sfuRoom) {
            return
        }

        let logstr = ''

        const p = this.sfuRoom.getParticipantByIdentity(sessionId)
        if (p && p instanceof RemoteParticipant) {
            p.videoTrackPublications.forEach((pub) => {
                if (!pub.isSubscribed) {
                    const username = this.peerInfoMap[sessionId]?.username || '?'
                    logstr += `\n    - resubscribing to video track for sessionId=${sessionId} (username=${username})`
                    pub.setSubscribed(true)
                }
            })
        }

        if (logstr) {
            log.verbose('sfuResubscribeRemotePeerVideo', `${logstr}`)
        }
    }

    private toJSON() {
        const list: { sessionId: string; username: string; isVisible: boolean | null }[] = []
        this.list.forEach((item) => {
            list.push({
                sessionId: item.sessionId,
                username: this.peerInfoMap[item.sessionId]?.username || '?',
                isVisible: item.isVisible,
            })
        })
        return list
    }
}

export const videoSubscriptionManager = new VideoSubscriptionManager()
